import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n';
import { findTerm, type GlossaryTerm } from '../features/glossary/terms';
import { isTopOverlay, popOverlay, pushOverlay } from './overlayStack';

/* --- <Term> — the inline glossary word -------------------------------------

   Wrap any piece of jargon in running copy: the word renders with a quiet
   dotted underline, and a tap opens a SMALL popover anchored to the word —
   title, one-line meaning, the Western equivalent when there is one, and a
   link to the full glossary entry. It is the light sibling of InfoDot/
   TermSheet in common.tsx: a sheet interrupts, this annotates.

   An unknown id renders its children as plain text — never a dead button,
   and never an eaten word. (InfoDot returns null for unknown terms; Term
   cannot, because it wraps real words in a sentence.)

   The popover is rendered through a portal to document.body so no ancestor
   transform/overflow can clip it. Two things do NOT survive that trip and
   are handled explicitly:
   - data-mode tradition palettes live on subtrees, so the trigger's nearest
     [data-mode] value is read at open time and stamped onto the popover root.
   - The light/dark palette needs nothing: [data-theme] lives on <html>,
     which is still an ancestor of a body portal.
*/

/** At most one popover open across the app; opening one closes the last. */
let activeClose: (() => void) | null = null;

interface PopPosition {
  top: number;
  left: number;
  /** Caret centre, px from the popover's left edge. */
  caretX: number;
  above: boolean;
}

/** Flip above the term when less than this much viewport remains below it. */
const FLIP_AT = 200;
/** Gap between the term and the popover, spanned by the caret. */
const GAP = 10;
/** Minimum distance from the viewport edges. */
const MARGIN = 8;

export function Term({ id, children }: { id: string; children: ReactNode }) {
  const entry = findTerm(id);
  // Unknown term: the words must still read as words.
  if (!entry) return <>{children}</>;
  return <KnownTerm entry={entry}>{children}</KnownTerm>;
}

function KnownTerm({ entry, children }: { entry: GlossaryTerm; children: ReactNode }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<string | null>(null);
  const [pos, setPos] = useState<PopPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    // Read the tradition palette at open time — the trigger cannot move
    // between traditions without a re-render closing the popover anyway.
    setMode(triggerRef.current?.closest('[data-mode]')?.getAttribute('data-mode') ?? null);
    setOpen(true);
  };

  // Single-open registry.
  useEffect(() => {
    if (!open) return;
    if (activeClose) activeClose();
    activeClose = close;
    return () => {
      if (activeClose === close) activeClose = null;
    };
  }, [open, close]);

  // Position: measure the trigger and the rendered popover, prefer below,
  // flip above when the space under the term is tight, clamp horizontally.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current;
      const pop = popRef.current;
      if (!trigger || !pop) return;
      const r = trigger.getBoundingClientRect();
      const pw = pop.offsetWidth;
      const ph = pop.offsetHeight;
      const centerX = r.left + r.width / 2;
      const maxLeft = Math.max(window.innerWidth - MARGIN - pw, MARGIN);
      const left = Math.min(Math.max(centerX - pw / 2, MARGIN), maxLeft);
      const above = window.innerHeight - r.bottom < FLIP_AT;
      setPos({
        top: above ? r.top - ph - GAP : r.bottom + GAP,
        left,
        // Keep the caret clear of the rounded corners.
        caretX: Math.min(Math.max(centerX - left, 14), Math.max(pw - 14, 14)),
        above,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  // Dismissal: outside pointerdown, Escape (restoring focus), any scroll —
  // repositioning against a moving page jitters, so a scroll simply closes.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    };
    // This popover is frequently the TOP layer over another one (it opens
    // from inside the kundli bhava popover). Escape dismisses only the
    // topmost — see components/overlayStack.ts.
    const token = pushOverlay('term-popover');
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopOverlay(token)) return;
      close();
      triggerRef.current?.focus();
    };
    const onScroll = () => close();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      popOverlay(token);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="term"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          // Terms sit inside clickable cards and headings.
          e.stopPropagation();
          toggle();
        }}
      >
        {children}
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label={entry.title}
            className={`termpop ${pos?.above ? 'termpop--above' : 'termpop--below'}`}
            data-mode={mode ?? undefined}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // Painted only once the first measure has run.
              visibility: pos ? undefined : 'hidden',
            }}
          >
            <span className="termpop__caret" aria-hidden style={{ left: pos?.caretX ?? 14 }} />
            <div className="h3">{entry.title}</div>
            <div className="small muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
              {entry.short}
            </div>
            {entry.western && (
              <div className="tiny faint" style={{ marginTop: 6 }}>
                {t('glossary.westernEquiv', { name: entry.western })}
              </div>
            )}
            <Link
              to={`/glossary#${entry.id}`}
              className="small"
              style={{ display: 'inline-block', marginTop: 8, color: 'var(--accent-ink)' }}
              onClick={close}
            >
              {t('glossary.fullStory')} →
            </Link>
          </div>,
          document.body,
        )}
    </>
  );
}
